// src/modules/app-views/_apps/appmulti/quotations/Form/Steps/CheckoutStep3.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Input,
  Row,
  Select,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { MdPayment } from 'react-icons/md';
import {
  parsePhoneToDisplay,
  parseZipToDisplay,
  returnOnlyNumbers,
} from 'utils/helpers';
import translateX from 'utils/translateX';
import utils from 'utils/utils';
import appmultiService from 'modules/app-views/_apps/appmulti/services/appmultiService';
import { calculatePlanValues } from 'modules/app-views/_apps/appmulti/services/pricingService';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import Icon from 'components/JarvislyComponents/Icon';
import bankSlipImg from 'assets/images/bank_slip.png';
import creditCardImg from 'assets/images/credit_card.png';
import pixImg from 'assets/images/pix.png';
import financialService from 'services/financialService';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import couponService from 'services/couponService';

const { useBreakpoint } = Grid;

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const sectionTitleStyle = { color: 'darkcyan' };
const sectionTitleIconStyle = { paddingTop: 4 };
const sectionTitleTextStyle = { marginLeft: 8 };
const imgStyle = {
  objectFit: 'cover',
  objectPosition: 'center',
  width: 56,
  margin: '28px auto 0',
  display: 'block',
};
const installmentLabelStyle = { fontSize: 14 };
const cardLabelStyle = { fontSize: 14 };
const cardSubtitleStyle = { fontSize: 14, padding: '24px 0 10px' };
const installmentSelectStyle = { height: 'unset', fontSize: 14 };
const paymentLinkBtnStyle = { height: 22 };
const actionBtnStyle = { width: 130 };
const couponRowStyle = { width: '100%' };
const couponInputStyle = { maxWidth: 200 };

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const MultiQuotationsCheckoutStep3 = props => {
  // HOOKS
  const {
    setStep,
    setIsGenerating,
    isGenerating,
    isMobile,
    buyer,
    travellers,
    plan,
    documentContext,
    order,
    setOrder,
    isDisabled,
    emergencyContacts,
    setPlan,
    paymentSelected,
    setPaymentSelected,
    creditCardInstallment,
    setCreditCardInstallment,
  } = props;

  const filter = documentContext?.filter || {};
  const isLarge = utils.getBreakPoint(useBreakpoint()).includes('lg');

  // STATE
  const [installmentsArr, setInstallmentsArr] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);

  const appliedCoupon = plan?.summary?.total?.couponDiscount;
  const couponIsApplied = !!appliedCoupon?.couponId;

  // EFFECTS
  useEffect(() => {
    const total = plan?.summary?.total?.netPriceWithCouponDiscount;
    if (!total) {
      const value = financialService.formatMoney(total, 2, 'brl', true);
      setInstallmentsArr([
        {
          value: '1X',
          label: (
            <span style={installmentLabelStyle}>{`1X DE ${value} (S/JUROS)`}</span>
          ),
        },
      ]);
    } else {
      const options = [];
      for (let i = 0; i < 6; i++) {
        const value = financialService.formatMoney(
          total / (i + 1),
          2,
          'brl',
          true,
        );
        options.push({
          value: `${i + 1}X`,
          label: (
            <span style={installmentLabelStyle}>
              {`${i + 1}X DE ${value} (S/JUROS)`}
            </span>
          ),
        });
      }
      setInstallmentsArr(options);
    }
  }, [plan?.summary?.total?.netPriceWithCouponDiscount]);

  // HANDLERS
  const onApplyCoupon = async () => {
    const code = (couponCode || '').trim();
    if (!code) return;
    setCouponApplying(true);
    try {
      const baseValue = plan?.summary?.total?.totalAdjusted;
      const result = await couponService.validate({
        code,
        planId: plan?._id,
        operatorId: plan?.operatorId,
        baseValue,
      });
      if (!result?.valid) {
        appService.message({
          flag: 'e',
          text: `coupon_${result?.rejectionReason || 'invalid_code'}`,
        });
        return;
      }
      setPlan &&
        setPlan(prev => {
          const total = { ...(prev?.summary?.total || {}) };
          total.couponDiscount = {
            couponId: result.couponId,
            type: result.discountType,
            discount: result.discount,
            discountValue: result.discountValue,
          };
          total.netPriceWithCouponDiscount = result.netValue;
          return { ...prev, summary: { ...prev.summary, total } };
        });
      appService.message({ flag: 's', text: 'coupon_applied' });
    } finally {
      setCouponApplying(false);
    }
  };

  const onRemoveCoupon = () => {
    setCouponCode('');
    setPlan &&
      setPlan(prev => {
        const total = { ...(prev?.summary?.total || {}) };
        total.couponDiscount = {
          couponId: null,
          type: 'percentage',
          discount: 0,
          discountValue: 0,
        };
        total.netPriceWithCouponDiscount = total.totalAdjusted;
        return { ...prev, summary: { ...prev.summary, total } };
      });
  };

  const onOpenPaymentUrl = () => {
    const link = order?.__receivables?.[0]?.paymentUrl || order?.paymentUrl;
    if (!link) return;
    window.open(link, '_blank');
  };

  const onGenerateOrder = () => {
    appService.modal({
      flag: 'c',
      title: 'attention',
      content: translateX('confirm_submit_text'),
      okText: translateX('confirm'),
      cancelText: translateX('cancel'),
      onOk: async () => {
        try {
          setIsGenerating(true);
          const result = await appmultiService.generateOrderFromQuotation(
            filter,
            plan,
            travellers,
            buyer,
            emergencyContacts,
            paymentSelected,
          );

          const entityPostUrl = API.ENTITIES.URL.replace(':_id?', '');
          const moreContacts = (result.travellers || []).map(t => ({
            entityId: t.entityId,
            note: 'Viajante',
          }));

          for (const c of result.emergencyContacts || []) {
            const [doc] = await axiosService.post({
              url: entityPostUrl,
              data: {
                name: c.name,
                dataProfile: ['contact'],
                dataType: 'person',
                mobile: c.phone,
              },
            });
            if (doc?._id) {
              moreContacts.push({
                entityId: doc._id,
                note: translateX('contato_de_emergencia'),
              });
            }
          }

          if (result.__buyer?._id) {
            const buyerUrl = API.ENTITIES.URL.replace(':_id?', result.__buyer._id);
            const [existing] = await axiosService.get({
              url: buyerUrl,
              returnAsObject: true,
            });
            const existingMore = existing?.moreContacts || [];
            const combined = existingMore
              .map(ex => moreContacts.find(c => c.entityId === ex.entityId) || ex)
              .concat(
                moreContacts.filter(
                  c => !existingMore.some(ex => ex.entityId === c.entityId),
                ),
              );
            await axiosService.put({
              url: buyerUrl,
              data: { moreContacts: combined },
            });
          }

          setOrder(result);
          setIsGenerating(false);
        } catch {
          // order generation failed — surface via the service notification; just
          // stop the spinner here (console logging is disallowed by the lint hook)
          setIsGenerating(false);
        }
      },
    });
  };

  const recalc = (paymentMethod, installment) => {
    const summary = calculatePlanValues({
      plan,
      days: filter.days,
      passengersArr: travellers,
      paymentMethod,
      creditCardInstallment: paymentMethod === 'credit_card' ? installment : 1,
    });
    setPlan && setPlan(prev => ({ ...prev, summary: { ...summary } }));
  };

  const onClickPaymentSelected = value => {
    if (value === paymentSelected) return;
    setPaymentSelected(value);
    recalc(value, creditCardInstallment);
  };

  const onClickCreditCardInstallment = parc => {
    setCreditCardInstallment(parc);
    recalc(paymentSelected, parc);
  };

  // RENDER HELPERS
  const cardStyle = sel => ({
    width: '100%',
    border: sel ? '2px solid darkcyan' : '',
    boxShadow: sel ? '0 4px 8px rgba(0, 0, 0, 0.1)' : '',
    pointerEvents: isDisabled ? 'none' : 'initial',
    cursor: isDisabled ? 'default' : 'pointer',
  });

  const buyerEmail = buyer?.email?.value || buyer?.email;
  const buyerPhone = buyer?.phone?.value || buyer?.phone;

  const items = [
    {
      key: 'buyer',
      label: translateX('buyer'),
      children: buyer?.name || buyer?.fullName,
    },
    {
      key: 'phone',
      label: translateX('phone'),
      children: parsePhoneToDisplay(buyerPhone),
    },
    { key: 'email', label: translateX('email'), children: buyerEmail },
    {
      key: 'address',
      label: translateX('address'),
      children: (
        <>
          <div>
            {buyer?.address1}, {buyer?.number}
            {buyer?.address2 ? <span>, {buyer.address2}</span> : null}
          </div>
          <div>
            {buyer?.city} -{' '}
            <span className="text-uppercase">{buyer?.region}</span>
          </div>
          <div>{parseZipToDisplay(buyer?.zip)}</div>
        </>
      ),
    },
    {
      key: 'travellers',
      label: translateX('travelers'),
      children: travellers?.map(t => (
        <div key={t.key}>
          {t.name || t.fullName}, {t.age} {translateX('years_old')}
          <br />
        </div>
      )),
    },
    {
      key: 'emergency',
      label: translateX('emergency_contacts'),
      children: emergencyContacts?.map((c, idx) => (
        <div key={idx}>
          <Badge
            status="default"
            text={`${c.name}, ${parsePhoneToDisplay(c.phone)}`}
          />
        </div>
      )),
    },
    {
      key: 'order',
      label: translateX('order'),
      children: order?._metadata?.__documentCode ? (
        <strong className="text-uppercase">
          {order._metadata.__documentCode}
        </strong>
      ) : (
        <span>N/A</span>
      ),
    },
    {
      key: 'status',
      label: translateX('status'),
      children: order?.status ? (
        <Badge
          status={order?.__badgeStatus || 'default'}
          text={translateX(order.status)}
        />
      ) : (
        <Badge status="default" text={translateX('waiting_for_generation')} />
      ),
    },
    {
      key: 'paymentLink',
      label: translateX('payment_link'),
      children:
        order?.__receivables?.[0]?.paymentUrl || order?.paymentUrl ? (
          <Button
            type="link"
            style={paymentLinkBtnStyle}
            className="p-0"
            onClick={onOpenPaymentUrl}
          >
            {translateX('view_billing')}
          </Button>
        ) : (
          <span>N/A</span>
        ),
    },
  ];

  // RENDER
  return (
    <div className="w-100">
      <Descriptions bordered items={items} className="w-100 mb-4" />

      <Spin spinning={isGenerating} size="large">
        <Row justify="center" className="w-100">
          <Col xs={24} lg={16} xl={16} className={isLarge ? 'pr-3' : ''}>
            <Card
              title={
                <Flex align="center" style={sectionTitleStyle}>
                  <div style={sectionTitleIconStyle}>
                    <Icon type={MdPayment} />
                  </div>
                  <div style={sectionTitleTextStyle}>
                    {translateX('select_payment_method')}
                  </div>
                </Flex>
              }
              className="w-100"
            >
              <Row justify="space-between">
                <Col xs={24} xl={12} xxl={8}>
                  <Row justify="center">
                    <Card
                      className="m-3"
                      style={cardStyle(paymentSelected === 'slip_pix')}
                      hoverable={!isDisabled}
                      onClick={() => onClickPaymentSelected('slip_pix')}
                      cover={
                        <img
                          alt={translateX('slip_pix')}
                          src={bankSlipImg}
                          style={imgStyle}
                        />
                      }
                    >
                      <div
                        className="text-center w-100 font-weight-bold"
                        style={cardLabelStyle}
                      >
                        {translateX('boleto_bancario')}
                      </div>
                      <div
                        className="text-center w-100 text-muted"
                        style={cardSubtitleStyle}
                      >
                        {translateX('discount_5_percent')}
                      </div>
                    </Card>
                  </Row>
                </Col>

                <Col xs={24} xl={12} xxl={8}>
                  <Row justify="center">
                    <Card
                      className="m-3"
                      style={cardStyle(paymentSelected === 'credit_card')}
                      hoverable={!isDisabled}
                      onClick={() => onClickPaymentSelected('credit_card')}
                      cover={
                        <img
                          alt={translateX('credit_card')}
                          src={creditCardImg}
                          style={imgStyle}
                        />
                      }
                    >
                      <div
                        className="text-center w-100 font-weight-bold"
                        style={cardLabelStyle}
                      >
                        {translateX('credit_card_label')}
                      </div>
                      <Select
                        className="pt-3 ant-select-selector-p8 w-100"
                        size="small"
                        style={installmentSelectStyle}
                        value={creditCardInstallment}
                        onChange={onClickCreditCardInstallment}
                        options={installmentsArr}
                      />
                    </Card>
                  </Row>
                </Col>

                <Col xs={24} xl={12} xxl={8}>
                  <Row justify="center">
                    <Card
                      className="m-3"
                      style={cardStyle(paymentSelected === 'pix')}
                      hoverable={!isDisabled}
                      onClick={() => onClickPaymentSelected('pix')}
                      cover={
                        <img
                          alt={translateX('pix')}
                          src={pixImg}
                          style={imgStyle}
                        />
                      }
                    >
                      <div
                        className="text-center w-100 font-weight-bold"
                        style={cardLabelStyle}
                      >
                        PIX
                      </div>
                      <div
                        className="text-center w-100 text-muted"
                        style={cardSubtitleStyle}
                      >
                        {translateX('discount_5_percent')}
                      </div>
                    </Card>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8} xl={8}>
            <Card
              title={
                <Flex align="center" style={sectionTitleStyle}>
                  <div>
                    <Icon type={ShoppingOutlined} />
                  </div>
                  <div style={sectionTitleTextStyle}>
                    {translateX('checkout')}
                  </div>
                </Flex>
              }
              className="w-100"
            >
              <Row justify="space-between">
                <Col span={24} className="text-right">
                  {/* COUPON */}
                  <Flex justify="space-between" align="center" className="mt-2">
                    {couponIsApplied ? (
                      <>
                        <div className="text-muted">
                          {translateX('coupon')}:{' '}
                          <span className="text-uppercase">{couponCode || ''}</span>
                          <Button
                            type="link"
                            size="small"
                            onClick={onRemoveCoupon}
                            className="pl-2"
                          >
                            {translateX('remove_coupon')}
                          </Button>
                        </div>
                        <div className="font-size-md text-danger">
                          -{' '}
                          {financialService.formatMoney({
                            value: appliedCoupon?.discountValue,
                            localeSymbol: 'brl',
                            resultToStr: true,
                          })}
                        </div>
                      </>
                    ) : (
                      <Flex gap={8} align="center" style={couponRowStyle}>
                        <Input
                          size="small"
                          placeholder={translateX('insert_coupon')}
                          value={couponCode}
                          onChange={e => setCouponCode(e.target.value)}
                          onPressEnter={onApplyCoupon}
                          style={couponInputStyle}
                        />
                        <Button size="small" loading={couponApplying} onClick={onApplyCoupon}>
                          {translateX('apply')}
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                  {/* END COUPON */}

                  <Flex justify="space-between" align="center" className="mt-2">
                    <div className="text-muted">TOTAL</div>
                    <div className="font-size-md">
                      {financialService.formatMoney({
                        value: plan?.summary?.total?.netPriceWithCouponDiscount,
                        localeSymbol: 'brl',
                        resultToStr: true,
                      })}
                    </div>
                  </Flex>
                  <Flex justify="space-between" align="center" className="mt-2">
                    <div className="text-muted text-uppercase">
                      {translateX('discount')} {translateX(paymentSelected)}{' '}
                      {paymentSelected === 'credit_card' ? '' : '(5%)'}
                    </div>
                    <div className="font-size-md text-danger">
                      -
                      {financialService.formatMoney({
                        value: plan?.summary?.total?.paymentDiscountValue,
                        localeSymbol: 'brl',
                        resultToStr: true,
                      })}
                    </div>
                  </Flex>
                  <Flex justify="space-between" align="center" className="mt-2">
                    <div className="text-muted">TOTAL A PAGAR</div>
                    <div className="font-size-md">
                      {financialService.formatMoney({
                        value: plan?.summary?.total?.totalToPay,
                        localeSymbol: 'brl',
                        resultToStr: true,
                      })}
                    </div>
                  </Flex>
                  <Flex justify="space-between" align="center" className="mt-2">
                    <div className="text-muted text-uppercase">
                      {translateX('payment_method')}
                    </div>
                    <div className="font-size-md">
                      {!paymentSelected
                        ? 'N/A'
                        : paymentSelected !== 'credit_card'
                          ? translateX(paymentSelected)
                          : creditCardInstallment === '1X'
                            ? translateX('credit_card_cash')
                            : `${creditCardInstallment} ${financialService.formatMoney(
                                plan?.summary?.total?.netPriceWithCouponDiscount /
                                  (returnOnlyNumbers(
                                    plan?.summary?.total?.paymentInstallment,
                                  ) || 1),
                                2,
                                'brl',
                                true,
                              )}`}
                    </div>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>

      {!isDisabled ? (
        <Flex
          justify={isMobile ? 'center' : 'flex-end'}
          align="center"
          className="mt-4"
        >
          <SimpleButton
            title="previous"
            className="mr-3"
            icon={<ArrowLeftOutlined />}
            style={actionBtnStyle}
            disabled={isDisabled}
            onClick={() => setStep(1)}
          />
          <SimpleButton
            title="confirm"
            type="primary"
            icon={<CheckOutlined />}
            style={actionBtnStyle}
            disabled={isDisabled || isGenerating || !paymentSelected}
            onClick={onGenerateOrder}
          />
        </Flex>
      ) : null}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationsCheckoutStep3;
